/**
 * Self Select Node - Player View
 *
 * "Choose your squad" experience where players voluntarily join teams.
 * Team columns with capacity indicators, pulsing empty slots as invite zones,
 * and an unassigned player pool. Smooth slide animations as players join.
 * Supports 2-8 teams, 2-100 players, party groups, desktop/mobile.
 * Uses reusable PlayerCard, TeamHeader, rank icons, and party colors.
 */

import type { QueueRankConfig } from "@rivaled/shared";
import { getTeamNames } from "@rivaled/shared";
import { AnimatePresence, motion } from "framer-motion";
import { Hand } from "lucide-react";
import { useMemo, useState } from "react";
import { cn } from "../../../lib/utils";
import { ErrorBanner } from "../../components/ErrorBanner";
import { NodeCard } from "../../components/NodeCard";
import { NodeHeader } from "../../components/NodeHeader";
import { PartyIcon } from "../../components/PartyIcon";
import { PlayerCard } from "../../components/PlayerCard";
import { TeamHeader } from "../../components/TeamHeader";
import { useCurrentPlayerId } from "../../hooks/useCurrentPlayerId";
import { getPartyColor } from "../../lib/party-colors";
import { getTeamColor } from "../../lib/team-colors";
import type { NodePlayerViewProps } from "../types";

type SelfSelectState = {
  teams: Record<number, string[]>;
  unassigned: string[];
  teamAssignments: Record<string, number>;
};

export function SelfSelectView({
  nodeState,
  players,
  context,
  onAction,
}: NodePlayerViewProps<SelfSelectState>) {
  const userId = useCurrentPlayerId();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const state = nodeState.state as SelfSelectState;
  const config = nodeState.config as {
    timeoutSeconds?: number;
    maxPerTeam?: number;
    allowUneven?: boolean;
    labels?: { title?: string };
  };
  const title = config.labels?.title ?? "Choose Your Team";
  const rankConfig = (context.rankConfig ?? null) as QueueRankConfig | null;

  const getPlayer = (id: string) => players.find((p) => p.userId === id);

  const teamEntries = Object.entries(state.teams);
  const teamCount = teamEntries.length;
  const teamSize = config.maxPerTeam ?? context.teamSize ?? Math.ceil(players.length / teamCount);

  // Seeded team names
  const teamNames = useMemo(
    () => getTeamNames(context.matchId, teamCount),
    [context.matchId, teamCount],
  );

  // Current player state
  const myTeam = teamEntries.find(([, ids]) => ids.includes(userId ?? ""))?.[0];
  const isUnassigned = state.unassigned.includes(userId ?? "");
  const myPartyId = getPlayer(userId ?? "")?.partyId ?? null;

  // Stats
  const totalAssigned = Object.values(state.teams).reduce((sum, ids) => sum + ids.length, 0);
  const totalPlayers = players.length;

  const handleSelectTeam = async (teamNum: number) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError(null);

    const result = await onAction("select_team", { team: teamNum, userId });

    if (!result.success && result.error) {
      setError(result.error);
    }

    setIsSubmitting(false);
  };

  return (
    <NodeCard>
      <NodeHeader
        title={title}
        subtitle={
          <AnimatePresence mode="wait">
            <motion.p
              key={isUnassigned ? "pick" : myTeam ? "assigned" : "watching"}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2 }}
              className="text-sm @sm:text-base"
            >
              {isUnassigned ? (
                <span className="text-brand-400 font-semibold">Select a team to join</span>
              ) : myTeam ? (
                <span className="text-text-secondary">Waiting for others to pick&hellip;</span>
              ) : (
                <span className="text-text-secondary">Watching team selection</span>
              )}
            </motion.p>
          </AnimatePresence>
        }
      />

      {/* Progress pill */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.15 }}
        className="text-center mb-6"
      >
        <div className="inline-flex items-center gap-3 px-4 py-2 rounded-lg bg-surface-800/80 border border-surface-700/50">
          <Hand size={16} className="text-brand-400" aria-hidden="true" />
          <span className="font-display text-2xl font-bold text-text-primary tabular-nums">
            {totalAssigned}
          </span>
          <span className="text-surface-500 text-sm">/</span>
          <span className="font-display text-2xl font-bold text-text-primary tabular-nums">
            {totalPlayers}
          </span>
          <span className="text-xs uppercase tracking-wider text-text-secondary font-medium">
            joined
          </span>
          <div className="w-px h-5 bg-surface-700/60" />
          <span className="font-display text-lg font-bold text-brand-400 tabular-nums">
            {teamCount}
          </span>
          <span className="text-xs uppercase tracking-wider text-text-secondary font-medium">
            {teamCount === 1 ? "team" : "teams"}
          </span>
        </div>
      </motion.div>

      <ErrorBanner error={error} />

      {/* Team panels */}
      <div
        className={cn(
          "grid gap-4 @md:gap-5",
          teamCount <= 2
            ? "grid-cols-1 @md:grid-cols-2"
            : teamCount === 3
              ? "grid-cols-1 @3xl:grid-cols-3"
              : "grid-cols-1 @md:grid-cols-2 @3xl:grid-cols-3",
        )}
      >
        {teamEntries.map(([teamNum, teamPlayerIds], teamIndex) => {
          const isMyTeam = teamNum === myTeam;
          const isFull = teamPlayerIds.length >= teamSize;
          const cardDelay = 0.1 + teamIndex * 0.08;
          const teamName = teamNames[teamIndex] ?? `Team ${teamNum}`;
          const emptySlots = Math.max(0, teamSize - teamPlayerIds.length);
          const canJoin = isUnassigned && !isFull && !isSubmitting;
          const totalElo = teamPlayerIds.reduce(
            (sum, id) => sum + (getPlayer(id)?.displayElo ?? 0),
            0,
          );
          const teamColor = getTeamColor(
            Number(teamNum),
            myTeam != null ? Number(myTeam) : undefined,
          );

          return (
            <motion.div
              key={teamNum}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: cardDelay }}
              className={cn(
                "rounded-lg border overflow-hidden transition-all duration-300",
                isMyTeam
                  ? "bg-surface-800/60 border-brand-500/25"
                  : canJoin
                    ? `bg-surface-800/50 ${teamColor.border} hover:border-brand-500/30 hover:shadow-[0_0_20px_rgba(38,217,181,0.06)]`
                    : `bg-surface-800/40 ${teamColor.border}`,
              )}
            >
              <TeamHeader
                teamName={teamName}
                isMyTeam={isMyTeam}
                rightContent={
                  <div className="flex items-center gap-2">
                    {/* Capacity indicator */}
                    <div className="flex items-center gap-1.5">
                      <div className="flex gap-0.5">
                        {Array.from({ length: teamSize }).map((_, i) => (
                          <div
                            key={i}
                            className={cn(
                              "w-1.5 h-1.5 rounded-full transition-colors duration-300",
                              i < teamPlayerIds.length
                                ? isMyTeam
                                  ? "bg-brand-400"
                                  : "bg-text-secondary"
                                : "bg-surface-600/40",
                            )}
                          />
                        ))}
                      </div>
                      <span
                        className={cn(
                          "text-xs tabular-nums font-medium",
                          isFull ? "text-amber-400/80" : "text-surface-500",
                        )}
                      >
                        {teamPlayerIds.length}/{teamSize}
                      </span>
                    </div>
                  </div>
                }
              />

              {/* Player rows */}
              <div className="p-2 @sm:p-3 space-y-1.5">
                <AnimatePresence>
                  {teamPlayerIds.map((playerId, playerIndex) => {
                    const player = getPlayer(playerId);
                    if (!player) return null;
                    const isMe = playerId === userId;
                    const isPartyMember =
                      !isMe && myPartyId !== null && player.partyId === myPartyId;
                    const partyColor = getPartyColor(player.partyId, myPartyId);

                    return (
                      <PlayerCard
                        key={playerId}
                        player={player}
                        variant="compact"
                        isMe={isMe}
                        partyColor={partyColor}
                        rankConfig={rankConfig}
                        animationDelay={cardDelay + 0.15 + playerIndex * 0.06}
                        badges={
                          isPartyMember ? (
                            <PartyIcon
                              size={12}
                              className={partyColor?.text ?? "text-amber-400/70"}
                            />
                          ) : null
                        }
                      />
                    );
                  })}
                </AnimatePresence>

                {/* Empty slots - pulsing invite zones */}
                {emptySlots > 0 &&
                  Array.from({ length: emptySlots }).map((_, i) => (
                    <motion.div
                      key={`empty-${i}`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: cardDelay + 0.3 + (teamPlayerIds.length + i) * 0.04 }}
                      className={cn(
                        "flex items-center gap-2.5 px-2.5 py-2 rounded-md border border-dashed",
                        canJoin
                          ? "border-brand-500/20 bg-brand-500/[0.03]"
                          : "border-surface-700/30",
                      )}
                    >
                      <div
                        className={cn(
                          "shrink-0 w-7 h-7 rounded-full border border-dashed",
                          canJoin ? "border-brand-500/30" : "border-surface-700/40",
                        )}
                      />
                      <span
                        className={cn(
                          "text-xs italic",
                          canJoin ? "text-brand-400/40" : "text-surface-600",
                        )}
                      >
                        Open slot
                      </span>
                    </motion.div>
                  ))}
              </div>

              {/* Team footer with join button or status */}
              <div className="px-4 py-2.5 border-t border-surface-700/20">
                {isUnassigned ? (
                  <motion.button
                    whileHover={canJoin ? { scale: 1.01 } : undefined}
                    whileTap={canJoin ? { scale: 0.99 } : undefined}
                    onClick={() => handleSelectTeam(Number(teamNum))}
                    disabled={!canJoin}
                    className={cn(
                      "w-full py-2 rounded-md text-sm font-semibold uppercase tracking-wider transition-all duration-200",
                      "focus:outline-none focus:ring-2 focus:ring-brand-400 focus:ring-offset-2 focus:ring-offset-surface-900",
                      canJoin
                        ? "bg-brand-500/15 text-brand-400 border border-brand-500/25 hover:bg-brand-500/25 hover:border-brand-500/40 cursor-pointer"
                        : isFull
                          ? "bg-surface-800/40 text-surface-500 border border-surface-700/20 cursor-not-allowed"
                          : "bg-surface-800/40 text-surface-500 border border-surface-700/20 cursor-not-allowed opacity-50",
                    )}
                  >
                    {isFull ? "Team Full" : isSubmitting ? "Joining..." : "Join Team"}
                  </motion.button>
                ) : (
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-wider text-surface-500 font-medium">
                      {teamPlayerIds.length} player{teamPlayerIds.length !== 1 ? "s" : ""}
                    </span>
                    <span className="text-[10px] uppercase tracking-wider text-surface-500 font-medium tabular-nums">
                      &Sigma; {totalElo}
                    </span>
                  </div>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Unassigned player pool */}
      {state.unassigned.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.4 }}
          className="mt-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <span className="font-display text-xs @sm:text-sm font-semibold uppercase tracking-wider text-text-secondary">
              Waiting to Pick
            </span>
            <div className="flex-1 h-px bg-surface-700/40" />
            <span className="text-xs text-surface-500 tabular-nums">
              {state.unassigned.length} remaining
            </span>
          </div>

          <div
            className={cn(
              "grid gap-2",
              state.unassigned.length <= 4
                ? "grid-cols-1 @md:grid-cols-2"
                : state.unassigned.length <= 9
                  ? "grid-cols-1 @md:grid-cols-2 @3xl:grid-cols-3"
                  : "grid-cols-1 @md:grid-cols-3 @3xl:grid-cols-4",
            )}
          >
            {state.unassigned.map((playerId, index) => {
              const player = getPlayer(playerId);
              if (!player) return null;
              const isMe = playerId === userId;
              const isPartyMember = !isMe && myPartyId !== null && player.partyId === myPartyId;
              const partyColor = getPartyColor(player.partyId, myPartyId);

              return (
                <PlayerCard
                  key={playerId}
                  player={player}
                  variant="compact"
                  isMe={isMe}
                  partyColor={partyColor}
                  rankConfig={rankConfig}
                  animationDelay={Math.min(0.35 + index * 0.04, 0.7)}
                  animateFrom="bottom"
                  badges={
                    isPartyMember ? (
                      <PartyIcon size={12} className={partyColor?.text ?? "text-amber-400/70"} />
                    ) : null
                  }
                />
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Waiting for current player */}
      {isUnassigned && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.4 }}
          className="mt-6 text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-500/[0.07] border border-brand-500/20">
            <div className="w-2 h-2 rounded-full bg-brand-400/70 animate-pulse" />
            <span className="text-sm text-text-secondary">Pick a team above to join</span>
          </div>
        </motion.div>
      )}

      {/* All assigned indicator */}
      {state.unassigned.length === 0 && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 + teamCount * 0.08, duration: 0.4 }}
          className="mt-6 text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-500/[0.07] border border-brand-500/20">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              className="text-brand-400"
              aria-hidden="true"
            >
              <path
                d="M5 13l4 4L19 7"
                stroke="currentColor"
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="text-sm text-text-secondary">
              All players{" "}
              <span className="font-display font-semibold text-brand-400">assigned</span>
            </span>
          </div>
        </motion.div>
      )}
    </NodeCard>
  );
}
